import { CSSProperties, memo, useEffect, useState } from 'react';
import { Input, MessagePlugin } from 'tdesign-react';

import { LibraryEdit } from '@/components/icon';
import { RenameType } from '@/interface/library';
import { CgiErrorCode } from '@/services/cgi';
import { updateLibraryGroup, updateUserResource } from '@/services/library';
import { useTypedSelector } from '@/store';

import styles from './index.module.less';

interface IShortcut {
  id: string;
  style?: CSSProperties;
  resourceName: string;
  type: RenameType;
  changeVisible: (visible: boolean) => void;
  visible: boolean;
  callBack?: (code: CgiErrorCode, info: {
    name: string, id: string
  }) => void
}

const LIMIT_NAME_LENGTH = 20;

const Shortcut: React.FC<IShortcut> = (props) => {
  const { resourceName, type, id, changeVisible, visible, callBack, style } = props;

  const libraryStore = useTypedSelector(store => store.library);

  useEffect(() => {
    if (id && libraryStore?.id && libraryStore?.id !== id) {
      handleClose();
    }
  }, [id, libraryStore?.id]);

  const [name, setName] = useState('');

  const [focus, setFocus] = useState(false);

  useEffect(() => () => {
    setName('');
  }, []);

  useEffect(() => {
    setName(resourceName);
  }, [resourceName]);

  const handleOpen = () => {
    changeVisible(true);
  };

  const onConfirm = () => {
    if (name?.trim() === resourceName?.trim()) {
      return;
    }
    if (name?.trim() === '') {
      MessagePlugin.error('名称不能为空');
      return;
    }
    if (type === RenameType.TYPE_RENAME_RESOURCE) {
      renameResource();
    }
    if (type === RenameType.TYPE_RENAME_DIRECTOR) {
      renameDirector();
    }
  };

  const renameDirector = async () => {
    if (id) {
      const { code, message } = await updateLibraryGroup({
        id,
        name,
      });

      if (code === CgiErrorCode.NO_ERROR) {
        MessagePlugin.success('重命名成功');
        callBack?.(code, {
          name,
          id,
        });
        handleClose();
        return;
      }
      MessagePlugin.error(message || `【${resourceName}】重命名失败，请重试`);
      return;
    }
  };

  const renameResource = async () => {
    if (id) {
      const { code, message } = await updateUserResource({
        resourceId: id,
        name,
      });

      if (code === CgiErrorCode.NO_ERROR) {
        MessagePlugin.success('重命名成功');
        callBack?.(code, {
          name,
          id,
        });
        handleClose();
        return;
      }
      MessagePlugin.error(message || `【${resourceName}】重命名失败，请重试`);
      return;
    }
  };

  const handleChange = (val: string) => {
    setName(val);
  };

  const handleFocus = () => {
    setFocus(true);
  };

  const handleBlur = () => {
    onConfirm();
  };

  const handleClose = () => {
    changeVisible(false);;
    setFocus(false);
    setName('');
  };

  useEffect(() => {
    document.addEventListener('click', handleWindowClick);
    return () => {
      document.removeEventListener('click', handleWindowClick);
    };
  }, []);

  const handleWindowClick = () => {
    handleClose();
  };

  const handleStopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <span className={styles.content} >
      {
        visible
          ? <div onClick={handleStopPropagation}>
            <Input
              onFocus={handleFocus}
              autofocus={true}
              className={styles.content}
              onBlur={handleBlur}
              value={name}
              onChange={handleChange}
              maxlength={focus ? LIMIT_NAME_LENGTH : undefined}
              showLimitNumber
              placeholder="请输入新名称"
              style={style}
            />
          </div> : <LibraryEdit onClick={handleOpen} />
      }
    </span>
  );
};

export default memo(Shortcut);
